import { useState, useEffect, useRef } from 'react';
import { productDetail, productFormTypes } from '../../../mocks/product';
import { useCart } from '@/hooks/useCart';
import { useFavorites } from '@/hooks/useFavorites';
import ProductGallery from './ProductGallery';

/* ─────────────── STOCK INDICATOR ─────────────── */
const StockIndicator = ({ stockCount, color }: { stockCount: number; color: string }) => {
  const maxStock = 50; // условный максимум для прогресс-бара
  const pct = Math.min(100, Math.round((stockCount / maxStock) * 100));

  // Цвет и статус в зависимости от остатка
  const getStatus = () => {
    if (stockCount <= 0) return { label: 'Нет в наличии', barColor: '#ff2d78', textColor: '#ff2d78', urgency: 'out' };
    if (stockCount <= 5) return { label: `Осталось ${stockCount} шт. — заканчивается!`, barColor: '#ff2d78', textColor: '#ff2d78', urgency: 'critical' };
    if (stockCount <= 15) return { label: `Осталось ${stockCount} шт. — мало`, barColor: '#ff6b00', textColor: '#ff6b00', urgency: 'low' };
    if (stockCount <= 30) return { label: `Осталось ${stockCount} шт.`, barColor: '#f0ff00', textColor: '#f0ff00', urgency: 'medium' };
    return { label: `В наличии ${stockCount} шт.`, barColor: '#39ff14', textColor: '#39ff14', urgency: 'high' };
  };

  const status = getStatus();
  const isCritical = status.urgency === 'critical' || status.urgency === 'out';

  return (
    <div
      className="px-3.5 py-3 rounded-xl"
      style={{
        background: `${status.barColor}06`,
        border: `1px solid ${status.barColor}${isCritical ? '40' : '22'}`,
        boxShadow: isCritical ? `0 0 10px ${status.barColor}15` : 'none',
      }}
    >
      {/* Header row */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-4 flex items-center justify-center">
            <i
              className={isCritical ? 'ri-alarm-warning-line' : 'ri-store-2-line'}
              style={{ color: status.barColor, fontSize: '13px' }}
            />
          </div>
          <span
            className="font-mono text-xs font-medium"
            style={{ color: status.textColor }}
          >
            {status.label}
          </span>
          {isCritical && (
            <span
              className="font-mono text-[9px] px-1.5 py-0.5 rounded-full animate-pulse"
              style={{
                background: `${status.barColor}18`,
                color: status.barColor,
                border: `1px solid ${status.barColor}35`,
              }}
            >
              Торопитесь
            </span>
          )}
        </div>
        <span className="font-orbitron font-bold text-xs" style={{ color: status.barColor }}>
          {pct}%
        </span>
      </div>

      {/* Progress bar */}
      <div
        className="relative h-1.5 rounded-full overflow-hidden"
        style={{ background: 'rgba(255,255,255,0.06)' }}
      >
        {/* Track fill */}
        <div
          className="h-full rounded-full transition-all duration-700 ease-out"
          style={{
            width: `${pct}%`,
            background: pct <= 30
              ? `linear-gradient(90deg, ${status.barColor}cc, ${status.barColor})`
              : `linear-gradient(90deg, ${color}80, ${status.barColor})`,
            boxShadow: `0 0 6px ${status.barColor}60`,
          }}
        />
        {/* Shimmer effect */}
        {pct > 0 && (
          <div
            className="absolute inset-y-0 left-0 rounded-full"
            style={{
              width: `${pct}%`,
              background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.25) 50%, transparent 100%)',
              backgroundSize: '200% 100%',
              animation: 'shimmer 2s infinite linear',
            }}
          />
        )}
      </div>

      {/* Scale labels */}
      <div className="flex justify-between mt-1.5">
        <span className="font-mono text-[9px] text-white/20">0 шт.</span>
        {stockCount > 0 && stockCount <= 15 && (
          <span className="font-mono text-[9px] text-white/30">
            {stockCount <= 5
              ? 'Последние экземпляры!'
              : 'Разбирают быстро'}
          </span>
        )}
        <span className="font-mono text-[9px] text-white/20">{maxStock}+ шт.</span>
      </div>
    </div>
  );
};

/* ─────────────── URGENCY BADGES ─────────────── */
const useAnimatedCounter = (target: number, duration = 800) => {
  const [value, setValue] = useState(target);
  const prevTarget = useRef(target);

  useEffect(() => {
    if (prevTarget.current === target) return;
    const start = prevTarget.current;
    const diff = target - start;
    const startTime = performance.now();

    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(start + diff * eased));
      if (progress < 1) requestAnimationFrame(tick);
      else prevTarget.current = target;
    };

    requestAnimationFrame(tick);
  }, [target, duration]);

  return value;
};

const UrgencyBadges = ({ color }: { color: string }) => {
  const [viewers, setViewers] = useState(() => Math.floor(Math.random() * 18) + 12);
  const [lastOrderMins, setLastOrderMins] = useState(() => Math.floor(Math.random() * 8) + 2);
  const [pulse, setPulse] = useState(false);
  const [orderPulse, setOrderPulse] = useState(false);
  const animatedViewers = useAnimatedCounter(viewers);

  // Viewers fluctuate every 5-12 seconds
  useEffect(() => {
    const schedule = () => {
      const delay = Math.random() * 7000 + 5000;
      return setTimeout(() => {
        setViewers(prev => {
          const delta = Math.random() < 0.55 ? 1 : -1;
          const next = Math.max(8, Math.min(42, prev + delta));
          if (next !== prev) setPulse(true);
          return next;
        });
        setPulse(true);
        setTimeout(() => setPulse(false), 600);
        timerRef.current = schedule();
      }, delay);
    };
    const timerRef = { current: schedule() };
    return () => clearTimeout(timerRef.current);
  }, []);

  // Last order timer ticks up every 60s, resets randomly
  useEffect(() => {
    const interval = setInterval(() => {
      setLastOrderMins(prev => {
        if (prev >= 15) {
          setOrderPulse(true);
          setTimeout(() => setOrderPulse(false), 800);
          return Math.floor(Math.random() * 3) + 1;
        }
        return prev + 1;
      });
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Simulate a new order every 2-5 minutes
  useEffect(() => {
    const schedule = () => {
      const delay = Math.random() * 180000 + 120000;
      return setTimeout(() => {
        setLastOrderMins(Math.floor(Math.random() * 3) + 1);
        setOrderPulse(true);
        setTimeout(() => setOrderPulse(false), 800);
        timerRef.current = schedule();
      }, delay);
    };
    const timerRef = { current: schedule() };
    return () => clearTimeout(timerRef.current);
  }, []);

  return (
    <div className="flex gap-2 flex-wrap">
      {/* Viewers badge */}
      <div
        className="flex items-center gap-2 px-3 py-2 rounded-xl flex-1 min-w-[160px]"
        style={{
          background: `${color}08`,
          border: `1px solid ${color}25`,
          transition: 'box-shadow 0.3s ease',
          boxShadow: pulse ? `0 0 12px ${color}35` : 'none',
        }}
      >
        <div className="relative w-5 h-5 flex items-center justify-center shrink-0">
          <i className="ri-eye-line" style={{ color, fontSize: '15px' }} />
          <span
            className="absolute -top-1 -right-1 w-2 h-2 rounded-full"
            style={{
              background: '#39ff14',
              boxShadow: '0 0 5px #39ff14',
              animation: 'ping 1.5s cubic-bezier(0,0,0.2,1) infinite',
            }}
          />
        </div>
        <div className="flex flex-col leading-none gap-0.5">
          <div className="flex items-baseline gap-1">
            <span
              className="font-orbitron font-black text-sm tabular-nums"
              style={{
                color,
                transition: 'transform 0.2s ease',
                transform: pulse ? 'scale(1.15)' : 'scale(1)',
                display: 'inline-block',
              }}
            >
              {animatedViewers}
            </span>
            <span className="font-mono text-[10px] text-white/45">чел. смотрят</span>
          </div>
          <span className="font-mono text-[9px] text-white/25">прямо сейчас</span>
        </div>
      </div>

      {/* Last order badge */}
      <div
        className="flex items-center gap-2 px-3 py-2 rounded-xl flex-1 min-w-[160px]"
        style={{
          background: 'rgba(57,255,20,0.05)',
          border: `1px solid rgba(57,255,20,${orderPulse ? '0.5' : '0.2'})`,
          transition: 'border-color 0.4s ease, box-shadow 0.4s ease',
          boxShadow: orderPulse ? '0 0 14px rgba(57,255,20,0.3)' : 'none',
        }}
      >
        <div className="w-5 h-5 flex items-center justify-center shrink-0">
          <i
            className="ri-shopping-bag-3-line"
            style={{
              color: '#39ff14',
              fontSize: '15px',
              transition: 'transform 0.3s ease',
              transform: orderPulse ? 'scale(1.25)' : 'scale(1)',
              display: 'inline-block',
            }}
          />
        </div>
        <div className="flex flex-col leading-none gap-0.5">
          <div className="flex items-baseline gap-1">
            <span
              className="font-orbitron font-black text-sm tabular-nums"
              style={{
                color: '#39ff14',
                transition: 'transform 0.3s ease',
                transform: orderPulse ? 'scale(1.15)' : 'scale(1)',
                display: 'inline-block',
              }}
            >
              {lastOrderMins}
            </span>
            <span className="font-mono text-[10px] text-white/45">
              {lastOrderMins === 1 ? 'мин. назад' : 'мин. назад'}
            </span>
          </div>
          <span className="font-mono text-[9px] text-white/25">последний заказ</span>
        </div>
      </div>
    </div>
  );
};

/* ─────────────── ONE-CLICK MODAL ─────────────── */
interface ModalProps { onClose: () => void; }

const OneClickModal = ({ onClose, price, name, color }: ModalProps & { price: number; name: string; color: string }) => {
  const [phone, setPhone] = useState('');
  const [sent, setSent] = useState(false);
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <div className="relative rounded-2xl p-8 w-full max-w-md" style={{ background: '#0d0d1a', border: `1px solid ${color}40`, boxShadow: `0 0 40px ${color}25` }} onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full cursor-pointer text-white/40 hover:text-white/80 transition-colors" style={{ background: 'rgba(255,255,255,0.05)' }}><i className="ri-close-line" /></button>
        {!sent ? (<>
          <div className="font-orbitron font-black text-lg mb-1" style={{ color }}>Купить в 1 клик</div>
          <p className="font-mono text-xs text-white/40 mb-6">{name}</p>
          <div className="space-y-4">
            <div>
              <label className="font-mono text-xs text-white/50 mb-1.5 block">Ваш телефон</label>
              <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+7 (___) ___-__-__" className="w-full px-4 py-3 rounded-xl font-mono text-sm text-white bg-transparent outline-none" style={{ border: `1px solid ${color}40`, background: 'rgba(255,255,255,0.03)' }} />
            </div>
            <div>
              <label className="font-mono text-xs text-white/50 mb-1.5 block">Ваше имя <span className="text-white/25">(необязательно)</span></label>
              <input type="text" placeholder="Как к вам обращаться?" className="w-full px-4 py-3 rounded-xl font-mono text-sm text-white bg-transparent outline-none" style={{ border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.03)' }} />
            </div>
            <div className="flex items-center justify-between p-4 rounded-xl" style={{ background: `${color}10`, border: `1px solid ${color}20` }}>
              <span className="font-mono text-sm text-white/60">К оплате:</span>
              <span className="font-orbitron font-black text-xl" style={{ color }}>{price.toLocaleString()} ₽</span>
            </div>
            <button onClick={() => phone.length >= 10 && setSent(true)} className="whitespace-nowrap w-full py-3.5 rounded-xl font-orbitron font-bold text-sm cursor-pointer transition-all duration-300" style={{ background: color, color: '#050508', boxShadow: `0 0 20px ${color}50` }}>
              Оформить заказ <i className="ri-arrow-right-line ml-1" />
            </button>
          </div>
          <p className="font-mono text-[10px] text-white/20 text-center mt-3">Перезвоним в течение 5 минут для подтверждения</p>
        </>) : (
          <div className="text-center py-6">
            <div className="w-16 h-16 flex items-center justify-center rounded-full mx-auto mb-4" style={{ background: `${color}20`, border: `2px solid ${color}` }}><i className="ri-check-line text-2xl" style={{ color }} /></div>
            <div className="font-orbitron font-bold text-lg text-white mb-2">Заказ принят!</div>
            <p className="font-mono text-sm text-white/50">Перезвоним по номеру <span style={{ color }}>{phone}</span> в течение 5 минут</p>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─────────────── INFOGRAPHIC ─────────────── */
interface InfographicItem { id: string; icon: string; label: string; allowed: boolean | null; accentColor: string; title: string; description: string; }

const INFOGRAPHIC_ITEMS: InfographicItem[] = [
  { id: 'pregnant', icon: 'ri-user-heart-line', label: 'Беременным', allowed: false, accentColor: '#ff2d78', title: 'Применение при беременности', description: 'Препарат противопоказан в период беременности и грудного вскармливания. Компоненты состава могут проникать через плацентарный барьер. Перед применением обязательно проконсультируйтесь с врачом.' },
  { id: 'driving', icon: 'ri-car-line', label: 'За рулём', allowed: false, accentColor: '#ff6b00', title: 'Управление транспортом', description: 'В период приёма следует соблюдать осторожность при управлении транспортом. В начале курса возможна лёгкая седация и снижение скорости реакции.' },
  { id: 'children', icon: 'ri-user-smile-line', label: 'От 5 лет', allowed: true, accentColor: '#39ff14', title: 'Применение у детей', description: 'Разрешён детям с 5 лет в дозировке 1/2 таблетки 1 раз в сутки во время еды. Курс и длительность определяет педиатр. Детям до 5 лет запрещено.' },
  { id: 'fridge', icon: 'ri-temp-cold-line', label: '+2°..+8°C', allowed: null, accentColor: '#00f5ff', title: 'Условия хранения', description: 'Хранить при +2°C...+8°C в оригинальной упаковке, защитив от света и влаги. Не замораживать. Срок годности — 2 года с даты производства.' },
];

const InfographicModal = ({ item, onClose }: { item: InfographicItem; onClose: () => void }) => (
  <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" onClick={onClose}>
    <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
    <div className="relative rounded-2xl p-8 w-full max-w-sm text-center" style={{ background: '#0d0d1a', border: `1px solid ${item.accentColor}40`, boxShadow: `0 0 40px ${item.accentColor}15` }} onClick={e => e.stopPropagation()}>
      <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full cursor-pointer text-white/40 hover:text-white/80 transition-colors" style={{ background: 'rgba(255,255,255,0.05)' }}><i className="ri-close-line" /></button>
      <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4 relative" style={{ background: `${item.accentColor}12`, border: `2px solid ${item.accentColor}40` }}>
        <i className={item.icon} style={{ color: item.accentColor, fontSize: '32px' }} />
        {item.allowed !== null && <div className="absolute -bottom-2 -right-2 w-7 h-7 rounded-full flex items-center justify-center" style={{ background: item.allowed ? '#39ff14' : '#ff2d78', boxShadow: `0 0 8px ${item.allowed ? '#39ff14' : '#ff2d78'}` }}><i className={item.allowed ? 'ri-check-line' : 'ri-close-line'} style={{ color: '#050508', fontSize: '13px' }} /></div>}
      </div>
      <div className="font-orbitron font-bold text-base text-white mb-2">{item.title}</div>
      {item.allowed !== null && <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-mono text-xs mb-4" style={{ background: item.allowed ? 'rgba(57,255,20,0.1)' : 'rgba(255,45,120,0.1)', color: item.allowed ? '#39ff14' : '#ff2d78', border: `1px solid ${item.allowed ? 'rgba(57,255,20,0.3)' : 'rgba(255,45,120,0.3)'}` }}><i className={item.allowed ? 'ri-check-line' : 'ri-close-line'} />{item.allowed ? 'Разрешено' : 'Запрещено'}</div>}
      {item.allowed === null && <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-mono text-xs mb-4" style={{ background: 'rgba(0,245,255,0.08)', color: '#00f5ff', border: '1px solid rgba(0,245,255,0.25)' }}><i className="ri-information-line" />Условия хранения</div>}
      <p className="font-mono text-sm text-white/55 leading-relaxed">{item.description}</p>
    </div>
  </div>
);

/* ─────────────── SHARE MODAL ─────────────── */
const ShareModal = ({ onClose, color, productName, productBrand }: ModalProps & { color: string; productName: string; productBrand: string }) => {
  const [copied, setCopied] = useState(false);
  const [copyAnim, setCopyAnim] = useState(false);
  const url = window.location.href;

  const handleCopy = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setCopyAnim(true);
      setTimeout(() => setCopyAnim(false), 300);
      setTimeout(() => setCopied(false), 3000);
    });
  };

  const handleNativeShare = () => {
    if (navigator.share) {
      navigator.share({ title: productName, text: `${productName} — ${productBrand}`, url });
    }
  };

  const hasNativeShare = typeof navigator !== 'undefined' && !!navigator.share;

  const shareLinks = [
    {
      icon: 'ri-telegram-line',
      label: 'Telegram',
      accentColor: '#2AABEE',
      href: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(productName + ' — ' + productBrand)}`,
    },
    {
      icon: 'ri-vk-line',
      label: 'ВКонтакте',
      accentColor: '#4C75A3',
      href: `https://vk.com/share.php?url=${encodeURIComponent(url)}&title=${encodeURIComponent(productName)}`,
    },
    {
      icon: 'ri-whatsapp-line',
      label: 'WhatsApp',
      accentColor: '#25D366',
      href: `https://wa.me/?text=${encodeURIComponent(productName + ' — ' + productBrand + '\n' + url)}`,
    },
    {
      icon: 'ri-mail-send-line',
      label: 'Email',
      accentColor: '#f0ff00',
      href: `mailto:?subject=${encodeURIComponent(productName + ' — ' + productBrand)}&body=${encodeURIComponent('Посмотри этот товар: ' + url)}`,
    },
    {
      icon: 'ri-twitter-x-line',
      label: 'X (Twitter)',
      accentColor: '#e7e9ea',
      href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(productName + ' — ' + productBrand)}`,
    },
    {
      icon: 'ri-links-line',
      label: 'Одноклассники',
      accentColor: '#ee8208',
      href: `https://connect.ok.ru/offer?url=${encodeURIComponent(url)}&title=${encodeURIComponent(productName)}`,
    },
  ];

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/85 backdrop-blur-md" />
      <div
        className="relative rounded-2xl w-full max-w-md overflow-hidden"
        style={{ background: '#0a0a14', border: `1px solid ${color}35`, boxShadow: `0 0 60px ${color}12, 0 24px 48px rgba(0,0,0,0.6)` }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header gradient strip */}
        <div className="h-1 w-full" style={{ background: `linear-gradient(90deg, ${color}, #ff2d78, ${color})` }} />

        <div className="p-6">
          {/* Title row */}
          <div className="flex items-start justify-between mb-5">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-7 h-7 flex items-center justify-center rounded-lg" style={{ background: `${color}15`, border: `1px solid ${color}30` }}>
                  <i className="ri-share-forward-line" style={{ color, fontSize: '15px' }} />
                </div>
                <span className="font-orbitron font-bold text-base text-white">Поделиться</span>
              </div>
              <p className="font-mono text-xs text-white/35 pl-9">{productName} · {productBrand}</p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer text-white/35 hover:text-white/70 transition-colors shrink-0"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
            >
              <i className="ri-close-line text-sm" />
            </button>
          </div>

          {/* URL copy block */}
          <div className="mb-5">
            <p className="font-mono text-[10px] text-white/30 mb-2 uppercase tracking-widest">Ссылка на товар</p>
            <div
              className="flex items-center gap-2 p-1 rounded-xl"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
            >
              <div className="flex-1 px-3 py-2 font-mono text-[11px] text-white/30 truncate">{url}</div>
              <button
                onClick={handleCopy}
                className="whitespace-nowrap flex items-center gap-1.5 px-4 py-2 rounded-lg font-mono text-xs cursor-pointer transition-all duration-300 shrink-0"
                style={
                  copied
                    ? { background: '#39ff14', color: '#050508', transform: copyAnim ? 'scale(0.95)' : 'scale(1)' }
                    : { background: color, color: '#050508', boxShadow: `0 0 12px ${color}45`, transform: copyAnim ? 'scale(0.95)' : 'scale(1)' }
                }
              >
                <i className={copied ? 'ri-check-double-line' : 'ri-file-copy-line'} />
                {copied ? 'Скопировано!' : 'Копировать'}
              </button>
            </div>
          </div>

          {/* Social grid */}
          <div className="mb-4">
            <p className="font-mono text-[10px] text-white/30 mb-3 uppercase tracking-widest">Поделиться в соцсетях</p>
            <div className="grid grid-cols-3 gap-2.5">
              {shareLinks.map(s => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="flex flex-col items-center gap-2 py-3 px-2 rounded-xl cursor-pointer transition-all duration-200 hover:scale-105 group"
                  style={{ background: `${s.accentColor}08`, border: `1px solid ${s.accentColor}20` }}
                >
                  <div
                    className="w-9 h-9 flex items-center justify-center rounded-xl transition-all duration-200 group-hover:scale-110"
                    style={{ background: `${s.accentColor}12`, border: `1px solid ${s.accentColor}25` }}
                  >
                    <i className={s.icon} style={{ color: s.accentColor, fontSize: '18px' }} />
                  </div>
                  <span className="font-mono text-[10px] text-white/40 text-center leading-tight">{s.label}</span>
                </a>
              ))}
            </div>
          </div>

          {/* Native share button (mobile) */}
          {hasNativeShare && (
            <button
              onClick={handleNativeShare}
              className="whitespace-nowrap w-full flex items-center justify-center gap-2 py-3 rounded-xl font-mono text-sm cursor-pointer transition-all duration-300 hover:opacity-90"
              style={{ background: `${color}12`, border: `1px solid ${color}30`, color }}
            >
              <i className="ri-share-box-line" />
              Другие приложения...
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─────────────── ERROR REPORT MODAL ─────────────── */
const ErrorReportModal = ({ onClose }: ModalProps) => {
  const [selected, setSelected] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [sent, setSent] = useState(false);
  const errorTypes = ['Неверная цена', 'Ошибка в названии или составе', 'Неверные противопоказания', 'Неправильное фото', 'Устаревшая информация', 'Другое'];
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <div className="relative rounded-2xl p-7 w-full max-w-md" style={{ background: '#0d0d1a', border: '1px solid rgba(255,107,0,0.35)', boxShadow: '0 0 40px rgba(255,107,0,0.1)' }} onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full cursor-pointer text-white/40 hover:text-white/80 transition-colors" style={{ background: 'rgba(255,255,255,0.05)' }}><i className="ri-close-line" /></button>
        {!sent ? (<>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ background: 'rgba(255,107,0,0.12)', border: '1px solid rgba(255,107,0,0.25)' }}><i className="ri-error-warning-line" style={{ color: '#ff6b00', fontSize: '18px' }} /></div>
            <span className="font-orbitron font-bold text-base text-white">Сообщить об ошибке</span>
          </div>
          <p className="font-mono text-xs text-white/35 mb-5 pl-11">Поможем исправить информацию о товаре</p>
          <div className="space-y-2 mb-4">
            {errorTypes.map(t => (
              <button key={t} onClick={() => setSelected(t)} className="w-full text-left px-4 py-2.5 rounded-xl font-mono text-sm cursor-pointer transition-all duration-200" style={{ background: selected === t ? 'rgba(255,107,0,0.08)' : 'rgba(255,255,255,0.02)', border: `1px solid ${selected === t ? 'rgba(255,107,0,0.45)' : 'rgba(255,255,255,0.06)'}`, color: selected === t ? '#ff6b00' : 'rgba(255,255,255,0.55)' }}>
                <div className="flex items-center gap-2">
                  <div className="w-3.5 h-3.5 rounded-full border flex items-center justify-center shrink-0" style={{ borderColor: selected === t ? '#ff6b00' : 'rgba(255,255,255,0.2)' }}>
                    {selected === t && <div className="w-2 h-2 rounded-full" style={{ background: '#ff6b00' }} />}
                  </div>
                  {t}
                </div>
              </button>
            ))}
          </div>
          <textarea value={comment} onChange={e => setComment(e.target.value)} maxLength={500} placeholder="Опишите ошибку подробнее (необязательно)..." className="w-full px-4 py-3 rounded-xl font-mono text-xs text-white/60 bg-transparent outline-none resize-none h-20 mb-4" style={{ border: '1px solid rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.02)' }} />
          <button onClick={() => selected && setSent(true)} className="whitespace-nowrap w-full py-3 rounded-xl font-orbitron font-bold text-sm transition-all duration-300" style={{ background: selected ? '#ff6b00' : 'rgba(255,255,255,0.04)', color: selected ? '#050508' : 'rgba(255,255,255,0.2)', boxShadow: selected ? '0 0 15px rgba(255,107,0,0.4)' : 'none', cursor: selected ? 'pointer' : 'not-allowed' }}>Отправить сообщение</button>
        </>) : (
          <div className="text-center py-6">
            <div className="w-16 h-16 flex items-center justify-center rounded-full mx-auto mb-4" style={{ background: 'rgba(255,107,0,0.12)', border: '2px solid #ff6b00' }}><i className="ri-check-line text-2xl" style={{ color: '#ff6b00' }} /></div>
            <div className="font-orbitron font-bold text-base text-white mb-2">Спасибо!</div>
            <p className="font-mono text-sm text-white/45">Сообщение отправлено. Исправим в ближайшее время.</p>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─────────────── PRICE DROP NOTIFY ─────────────── */
type NotifyStep = 'idle' | 'open' | 'loading' | 'success' | 'error';

const PriceDropNotify = ({ color, productName, currentPrice }: { color: string; productName: string; currentPrice: number }) => {
  const [step, setStep] = useState<NotifyStep>('idle');
  const [email, setEmail] = useState('');
  const [targetPrice, setTargetPrice] = useState(Math.floor(currentPrice * 0.85));
  const [emailError, setEmailError] = useState('');

  const validateEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateEmail(email)) { setEmailError('Введите корректный email'); return; }
    setEmailError('');
    setStep('loading');
    try {
      const body = new URLSearchParams();
      body.append('email', email);
      body.append('product', productName);
      body.append('current_price', String(currentPrice));
      body.append('target_price', String(targetPrice));
      const res = await fetch('https://readdy.ai/api/form/d79b7qb2hsar0k5m0lag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setStep(res.ok ? 'success' : 'error');
    } catch {
      setStep('error');
    }
  };

  if (step === 'idle') {
    return (
      <button
        onClick={() => setStep('open')}
        className="whitespace-nowrap w-full flex items-center justify-between px-4 py-3 rounded-xl cursor-pointer transition-all duration-200 group hover:scale-[1.01]"
        style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}
      >
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 flex items-center justify-center rounded-lg" style={{ background: 'rgba(57,255,20,0.08)', border: '1px solid rgba(57,255,20,0.2)' }}>
            <i className="ri-notification-3-line" style={{ color: '#39ff14', fontSize: '14px' }} />
          </div>
          <div className="text-left">
            <p className="font-mono text-xs text-white/65 leading-none mb-0.5">Уведомить о снижении цены</p>
            <p className="font-mono text-[10px] text-white/28">Пришлём письмо, когда цена упадёт</p>
          </div>
        </div>
        <i className="ri-arrow-right-s-line text-white/25 group-hover:text-white/50 transition-colors" />
      </button>
    );
  }

  if (step === 'success') {
    return (
      <div
        className="flex items-center gap-3 px-4 py-3 rounded-xl"
        style={{ background: 'rgba(57,255,20,0.06)', border: '1px solid rgba(57,255,20,0.25)' }}
      >
        <div className="w-8 h-8 flex items-center justify-center rounded-full shrink-0" style={{ background: 'rgba(57,255,20,0.12)', border: '1px solid rgba(57,255,20,0.3)' }}>
          <i className="ri-check-line" style={{ color: '#39ff14', fontSize: '16px' }} />
        </div>
        <div>
          <p className="font-mono text-xs font-bold" style={{ color: '#39ff14' }}>Подписка оформлена!</p>
          <p className="font-mono text-[10px] text-white/35 mt-0.5">Пришлём письмо на <span className="text-white/55">{email}</span>, когда цена снизится до {targetPrice.toLocaleString()} ₽</p>
        </div>
      </div>
    );
  }

  return (
    <form
      data-readdy-form
      id="price-drop-notification"
      onSubmit={handleSubmit}
      className="rounded-xl overflow-hidden"
      style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${color}25` }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 flex items-center justify-center rounded-lg" style={{ background: 'rgba(57,255,20,0.08)', border: '1px solid rgba(57,255,20,0.2)' }}>
            <i className="ri-notification-3-line" style={{ color: '#39ff14', fontSize: '13px' }} />
          </div>
          <span className="font-mono text-xs text-white/70 font-medium">Уведомить о снижении цены</span>
        </div>
        <button
          type="button"
          onClick={() => setStep('idle')}
          className="w-6 h-6 flex items-center justify-center rounded-full cursor-pointer text-white/30 hover:text-white/60 transition-colors"
          style={{ background: 'rgba(255,255,255,0.04)' }}
        >
          <i className="ri-close-line text-xs" />
        </button>
      </div>

      <div className="p-4 space-y-3">
        {/* Target price slider */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="font-mono text-[10px] text-white/40 uppercase tracking-wider">Желаемая цена</label>
            <span className="font-orbitron font-bold text-sm" style={{ color }}>{targetPrice.toLocaleString()} ₽</span>
          </div>
          <div className="relative">
            <input
              type="range"
              min={Math.floor(currentPrice * 0.5)}
              max={Math.floor(currentPrice * 0.95)}
              step={10}
              value={targetPrice}
              onChange={e => setTargetPrice(Number(e.target.value))}
              className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, ${color} 0%, ${color} ${((targetPrice - Math.floor(currentPrice * 0.5)) / (Math.floor(currentPrice * 0.95) - Math.floor(currentPrice * 0.5))) * 100}%, rgba(255,255,255,0.1) ${((targetPrice - Math.floor(currentPrice * 0.5)) / (Math.floor(currentPrice * 0.95) - Math.floor(currentPrice * 0.5))) * 100}%, rgba(255,255,255,0.1) 100%)`,
              }}
            />
          </div>
          <div className="flex justify-between font-mono text-[9px] text-white/22 mt-1">
            <span>−50% · {Math.floor(currentPrice * 0.5).toLocaleString()} ₽</span>
            <span className="text-white/18">Сейчас: {currentPrice.toLocaleString()} ₽</span>
          </div>
        </div>

        {/* Savings badge */}
        <div
          className="flex items-center justify-between px-3 py-2 rounded-lg"
          style={{ background: 'rgba(57,255,20,0.05)', border: '1px solid rgba(57,255,20,0.12)' }}
        >
          <span className="font-mono text-[10px] text-white/40">Экономия при снижении:</span>
          <span className="font-orbitron font-bold text-xs" style={{ color: '#39ff14' }}>
            −{(currentPrice - targetPrice).toLocaleString()} ₽ ({Math.round((1 - targetPrice / currentPrice) * 100)}%)
          </span>
        </div>

        {/* Email input */}
        <div>
          <label className="font-mono text-[10px] text-white/40 uppercase tracking-wider mb-1.5 block">Email для уведомления</label>
          <input
            type="email"
            name="email"
            value={email}
            onChange={e => { setEmail(e.target.value); setEmailError(''); }}
            placeholder="your@email.com"
            className="w-full px-3 py-2.5 rounded-xl font-mono text-sm text-white bg-transparent outline-none transition-all duration-200"
            style={{
              border: emailError ? '1px solid rgba(255,45,120,0.6)' : `1px solid ${color}30`,
              background: 'rgba(255,255,255,0.03)',
              fontSize: '13px',
            }}
          />
          {emailError && (
            <p className="font-mono text-[10px] mt-1" style={{ color: '#ff2d78' }}>
              <i className="ri-error-warning-line mr-1" />{emailError}
            </p>
          )}
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={step === 'loading'}
          className="whitespace-nowrap w-full py-2.5 rounded-xl font-mono text-xs font-bold cursor-pointer transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-60"
          style={{ background: `${color}18`, border: `1px solid ${color}40`, color }}
        >
          {step === 'loading' ? (
            <><i className="ri-loader-4-line animate-spin" />Подписываем...</>
          ) : (
            <><i className="ri-notification-badge-line" />Подписаться на снижение цены</>
          )}
        </button>

        {step === 'error' && (
          <p className="font-mono text-[10px] text-center" style={{ color: '#ff2d78' }}>
            <i className="ri-error-warning-line mr-1" />Ошибка отправки. Попробуйте ещё раз.
          </p>
        )}

        <p className="font-mono text-[9px] text-white/20 text-center leading-relaxed">
          Отправим одно письмо при достижении цены. Без спама.
        </p>
      </div>
    </form>
  );
};

/* ─────────────── BONUS BLOCK ─────────────── */
const BonusBlock = ({ bonusPoints, quantity, color }: { bonusPoints: number; quantity: number; color: string }) => {
  const [expanded, setExpanded] = useState(false);
  const total = bonusPoints * quantity;
  return (
    <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(240,255,0,0.18)', background: 'rgba(240,255,0,0.025)' }}>
      <button onClick={() => setExpanded(e => !e)} className="w-full flex items-center justify-between px-3 py-2.5 cursor-pointer">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 flex items-center justify-center rounded-lg" style={{ background: 'rgba(240,255,0,0.1)', border: '1px solid rgba(240,255,0,0.18)' }}>
            <i className="ri-gift-2-line" style={{ color: '#f0ff00', fontSize: '13px' }} />
          </div>
          <span className="font-mono text-xs text-white">
            <span style={{ color: '#f0ff00' }} className="font-bold">+{total} баллов</span>
            <span className="text-white/40"> за покупку</span>
          </span>
        </div>
        <i className={expanded ? 'ri-arrow-up-s-line text-white/30 text-sm' : 'ri-arrow-down-s-line text-white/30 text-sm'} />
      </button>
      {expanded && (
        <div className="px-3 pb-3 space-y-2.5" style={{ borderTop: '1px solid rgba(240,255,0,0.08)' }}>
          <div className="pt-2.5 grid grid-cols-3 gap-1.5">
            {[
              { label: 'Начислится', value: `${total} балл.`, sub: '1 балл = 1 ₽' },
              { label: 'На счёте', value: '1 248 балл.', sub: 'ваш баланс' },
              { label: 'Уровень', value: 'SILVER', sub: 'до GOLD: 752' },
            ].map((item, i) => (
              <div key={i} className="flex flex-col gap-0.5 p-2 rounded-lg" style={{ background: 'rgba(240,255,0,0.04)', border: '1px solid rgba(240,255,0,0.09)' }}>
                <span className="font-mono text-[9px] text-white/30">{item.label}</span>
                <span className="font-orbitron font-bold text-xs" style={{ color: '#f0ff00' }}>{item.value}</span>
                <span className="font-mono text-[9px] text-white/22">{item.sub}</span>
              </div>
            ))}
          </div>
          <div>
            <div className="flex justify-between font-mono text-[9px] text-white/28 mb-1"><span>SILVER</span><span>GOLD (2000)</span></div>
            <div className="h-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.05)' }}>
              <div className="h-full rounded-full" style={{ width: '62%', background: 'linear-gradient(90deg, #f0ff00, #ff6b00)' }} />
            </div>
          </div>
          <p className="font-mono text-[9px] text-white/28 leading-relaxed">Баллы начисляются после получения. До 30% заказа можно оплатить баллами.</p>
        </div>
      )}
    </div>
  );
};

/* ─────────────── PHARMACIST BLOCK ─────────────── */
const PharmacistBlock = () => (
  <div className="flex items-start gap-3 p-4 rounded-xl" style={{ background: 'rgba(0,245,255,0.03)', border: '1px solid rgba(0,245,255,0.15)' }}>
    <div className="w-12 h-12 rounded-xl overflow-hidden shrink-0" style={{ border: '2px solid rgba(0,245,255,0.35)', boxShadow: '0 0 10px rgba(0,245,255,0.15)' }}>
      <img src="https://public.readdy.ai/ai/img_res/f8136c74f173ee0e5d881fc902ce702b.jpg" alt="Фармацевт Иванов И.И." className="w-full h-full object-cover object-top" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-1.5 mb-0.5">
        <div className="w-4 h-4 flex items-center justify-center"><i className="ri-shield-check-fill" style={{ color: '#00f5ff', fontSize: '14px' }} /></div>
        <span className="font-mono text-xs text-white/60">Проверено фармацевтом</span>
      </div>
      <p className="font-mono text-sm text-white/85 font-medium leading-tight">Иванов Иван Иванович</p>
      <p className="font-mono text-[11px] text-white/35 leading-snug mt-0.5">Профессор медицинских наук, МГУ · Фармаколог высшей категории</p>
      <div className="flex items-center gap-2 mt-2">
        <span className="font-mono text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(0,245,255,0.07)', color: '#00f5ff', border: '1px solid rgba(0,245,255,0.18)' }}>Актуально</span>
        <span className="font-mono text-[10px] text-white/22">март 2026</span>
      </div>
    </div>
  </div>
);

/* ─────────────── FORM & DOSAGE SELECTOR ─────────────── */
interface DosageOption { id: string; label: string; price: number; oldPrice: number; inStock: boolean; stockCount: number; }
interface FormType { id: string; label: string; icon: string; dosages: DosageOption[]; }

interface FormDosageSelectorProps {
  formTypes: FormType[];
  selectedFormId: string;
  selectedDosageId: string;
  color: string;
  onFormChange: (formType: FormType) => void;
  onDosageChange: (dosage: DosageOption) => void;
}

const FormDosageSelector = ({ formTypes, selectedFormId, selectedDosageId, color, onFormChange, onDosageChange }: FormDosageSelectorProps) => {
  const currentForm = formTypes.find(f => f.id === selectedFormId) ?? formTypes[0];

  return (
    <div className="space-y-3">
      {/* Form type */}
      <div>
        <p className="font-mono text-xs text-white/40 mb-2">Форма выпуска</p>
        <div className="flex gap-2 flex-wrap">
          {formTypes.map(form => {
            const isActive = form.id === selectedFormId;
            return (
              <button
                key={form.id}
                onClick={() => onFormChange(form)}
                className="whitespace-nowrap flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-mono text-xs cursor-pointer transition-all duration-200"
                style={{
                  background: isActive ? `${color}14` : 'rgba(255,255,255,0.03)',
                  border: isActive ? `1.5px solid ${color}` : '1.5px solid rgba(255,255,255,0.1)',
                  color: isActive ? color : 'rgba(255,255,255,0.5)',
                  boxShadow: isActive ? `0 0 8px ${color}25` : 'none',
                }}
              >
                <i className={form.icon} style={{ fontSize: '13px' }} />
                {form.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Dosage */}
      <div>
        <p className="font-mono text-xs text-white/40 mb-2">Дозировка и фасовка</p>
        <div className="flex gap-2 flex-wrap">
          {currentForm.dosages.map(dosage => {
            const isActive = dosage.id === selectedDosageId;
            const isOos = !dosage.inStock;
            return (
              <button
                key={dosage.id}
                onClick={() => !isOos && onDosageChange(dosage)}
                className="whitespace-nowrap flex flex-col items-start px-3 py-2 rounded-lg font-mono cursor-pointer transition-all duration-200 relative"
                style={{
                  background: isActive ? `${color}14` : isOos ? 'rgba(255,255,255,0.01)' : 'rgba(255,255,255,0.03)',
                  border: isActive ? `1.5px solid ${color}` : '1.5px solid rgba(255,255,255,0.08)',
                  boxShadow: isActive ? `0 0 8px ${color}25` : 'none',
                  opacity: isOos ? 0.4 : 1,
                  cursor: isOos ? 'not-allowed' : 'pointer',
                }}
              >
                <span className="text-xs leading-tight" style={{ color: isActive ? color : 'rgba(255,255,255,0.65)' }}>{dosage.label}</span>
                <span className="text-[10px]" style={{ color: isActive ? `${color}cc` : 'rgba(255,255,255,0.35)' }}>{dosage.price.toLocaleString()} ₽</span>
                {isOos && (
                  <span className="absolute -top-1.5 -right-1.5 font-mono text-[8px] px-1 py-0.5 rounded-full" style={{ background: 'rgba(255,45,120,0.15)', color: '#ff2d78', border: '1px solid rgba(255,45,120,0.3)' }}>нет</span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/* ─────────────── MAIN COMPONENT ─────────────── */
const ProductHero = () => {
  const product = productDetail;
  const color = product.color;

  const { addItem, hasItem } = useCart();
  const { toggleFavorite, isFavorite } = useFavorites();
  const [selectedFormId, setSelectedFormId] = useState(productFormTypes[0].id);
  const [selectedDosageId, setSelectedDosageId] = useState(productFormTypes[0].dosages[0].id);
  const [quantity, setQuantity] = useState(1);
  const [showOneClick, setShowOneClick] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showErrorReport, setShowErrorReport] = useState(false);
  const [activeInfographic, setActiveInfographic] = useState<InfographicItem | null>(null);

  const currentForm = productFormTypes.find(f => f.id === selectedFormId) ?? productFormTypes[0];
  const currentDosage = currentForm.dosages.find(d => d.id === selectedDosageId) ?? currentForm.dosages[0];

  const handleFormChange = (form: FormType) => {
    setSelectedFormId(form.id);
    const firstInStock = form.dosages.find(d => d.inStock) ?? form.dosages[0];
    setSelectedDosageId(firstInStock.id);
    setQuantity(1);
  };

  const handleDosageChange = (dosage: DosageOption) => {
    setSelectedDosageId(dosage.id);
    setQuantity(1);
  };

  const totalPrice = currentDosage.price * quantity;
  const saving = (currentDosage.oldPrice - currentDosage.price) * quantity;
  const maxQty = currentDosage.stockCount;

  const benefits = [
    { icon: 'ri-brain-line', text: 'Улучшает память и концентрацию' },
    { icon: 'ri-battery-charge-line', text: 'Снижает умственную усталость' },
    { icon: 'ri-moon-line', text: 'Нормализует сон' },
    { icon: 'ri-leaf-line', text: 'Натуральный состав' },
  ];

  return (
    <>
      {showOneClick && <OneClickModal onClose={() => setShowOneClick(false)} price={totalPrice} name={`${product.name} — ${currentForm.label}, ${currentDosage.label}`} color={color} />}
      {showShare && <ShareModal onClose={() => setShowShare(false)} color={color} productName={product.name} productBrand={product.brand ?? 'NeonPharma'} />}
      {showErrorReport && <ErrorReportModal onClose={() => setShowErrorReport(false)} />}
      {activeInfographic && <InfographicModal item={activeInfographic} onClose={() => setActiveInfographic(null)} />}

      <section className="pt-6 pb-12 px-4 md:px-8 lg:px-12" style={{ background: '#050508' }}>
        <div className="max-w-7xl mx-auto">

          {/* Breadcrumbs */}
          <nav className="flex items-center gap-2 font-mono text-xs text-white/25 mb-5 flex-wrap">
            <a href="/" className="hover:text-white/55 cursor-pointer transition-colors">Главная</a>
            <i className="ri-arrow-right-s-line" />
            <a href="/catalog" className="hover:text-white/55 cursor-pointer transition-colors">Каталог</a>
            <i className="ri-arrow-right-s-line" />
            <a href="/catalog/section/lekarstva" className="hover:text-white/55 cursor-pointer transition-colors">Лекарства</a>
            <i className="ri-arrow-right-s-line" />
            <a
              href={`/catalog?section=${encodeURIComponent('Лекарства')}&category=${encodeURIComponent('Нейропротекторы')}`}
              className="cursor-pointer transition-colors hover:text-white/55"
            >
              Нейропротекторы
            </a>
            <i className="ri-arrow-right-s-line" />
            <span style={{ color }}>{product.name}</span>
          </nav>

          {/* Medical disclaimer */}
          <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl mb-6" style={{ background: 'rgba(255,107,0,0.06)', border: '1px solid rgba(255,107,0,0.22)' }}>
            <div className="w-5 h-5 flex items-center justify-center shrink-0">
              <i className="ri-stethoscope-line" style={{ color: '#ff6b00', fontSize: '16px' }} />
            </div>
            <p className="font-mono text-sm text-white/60 leading-snug">
              <strong className="text-white/80">Есть противопоказания</strong> — перед применением проконсультируйтесь с врачом или фармацевтом
            </p>
          </div>

          {/* ═══════════════ TWO-COLUMN GRID ═══════════════ */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] xl:grid-cols-[1fr_460px] gap-8 xl:gap-12 items-start">

            {/* ════ LEFT: Gallery + Info ════ */}
            <div className="flex flex-col gap-5">

              {/* Gallery with zoom, video, lightbox */}
              <ProductGallery
                images={product.images}
                badge={product.badge}
                badgeColor={product.badgeColor}
                discount={product.discount}
                color={color}
                productName={product.name}
              />

              {/* Benefits */}
              <div className="flex flex-wrap gap-2">
                {benefits.map((b, i) => (
                  <div key={i} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <div className="w-4 h-4 flex items-center justify-center"><i className={b.icon} style={{ color, fontSize: '13px' }} /></div>
                    <span className="font-mono text-xs text-white/55">{b.text}</span>
                  </div>
                ))}
              </div>

              {/* Description */}
              <p className="text-sm text-white/50 leading-relaxed" style={{ fontFamily: 'Share Tech Mono, monospace' }}>{product.description}</p>

              {/* Pharmacist block */}
              <PharmacistBlock />
            </div>

            {/* ════ RIGHT: Purchase panel (sticky) ════ */}
            <div
              className="flex flex-col gap-4 lg:sticky lg:self-start"
              style={{ top: '8px' }}
            >
              {/* Brand + share */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs text-white/35 px-2.5 py-1 rounded-full" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>{product.brand}</span>
                  <span className="font-mono text-xs text-white/25">Арт. {product.sku}</span>
                </div>
                <button
                  onClick={() => setShowShare(true)}
                  className="whitespace-nowrap flex items-center gap-1.5 px-3 py-1.5 rounded-full cursor-pointer transition-all duration-200 font-mono text-xs hover:scale-105 group"
                  style={{ background: `${color}10`, border: `1px solid ${color}30`, color: `${color}cc` }}
                >
                  <i className="ri-share-forward-line group-hover:rotate-12 transition-transform duration-200" style={{ fontSize: '13px' }} />
                  Поделиться
                </button>
              </div>

              {/* Name */}
              <div>
                <h1 className="font-orbitron font-black text-2xl md:text-3xl leading-tight text-white mb-1.5" style={{ textShadow: `0 0 18px ${color}35` }}>
                  {product.name}
                </h1>
                <p className="font-mono text-xs text-white/40">{product.subtitle}</p>
              </div>

              {/* Rating */}
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <div className="flex gap-0.5">{[1,2,3,4,5].map(s => <i key={s} className={`text-xs ${s <= Math.round(product.rating) ? 'ri-star-fill' : 'ri-star-line'}`} style={{ color: '#f0ff00' }} />)}</div>
                  <span className="font-orbitron font-bold text-sm" style={{ color: '#f0ff00' }}>{product.rating}</span>
                  <span className="font-mono text-xs text-white/30">({product.reviewsCount})</span>
                </div>
                <div className="flex items-center gap-1.5 ml-auto">
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{
                      background: currentDosage.inStock ? '#39ff14' : '#ff2d78',
                      boxShadow: `0 0 5px ${currentDosage.inStock ? '#39ff14' : '#ff2d78'}`,
                    }}
                  />
                  <span className="font-mono text-xs" style={{ color: currentDosage.inStock ? '#39ff14' : '#ff2d78' }}>
                    {currentDosage.inStock ? 'В наличии' : 'Нет в наличии'}
                  </span>
                </div>
              </div>

              {/* Stock indicator */}
              <StockIndicator stockCount={currentDosage.stockCount} color={color} />

              {/* Urgency: viewers + last order */}
              <UrgencyBadges color={color} />

              {/* Price block */}
              <div className="px-4 py-3 rounded-xl" style={{ background: `${color}08`, border: `1px solid ${color}20` }}>
                <div className="flex items-end gap-3 mb-1.5">
                  <span className="font-orbitron font-black text-3xl leading-none" style={{ color, textShadow: `0 0 18px ${color}70` }}>
                    {currentDosage.price.toLocaleString()} ₽
                  </span>
                  <span className="font-mono text-sm text-white/25 line-through mb-0.5">{currentDosage.oldPrice.toLocaleString()} ₽</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(57,255,20,0.1)', color: '#39ff14', border: '1px solid rgba(57,255,20,0.25)' }}>
                    Скидка {saving.toLocaleString()} ₽
                  </span>
                  {quantity > 1 && (
                    <span className="font-mono text-xs text-white/35">× {quantity} = {totalPrice.toLocaleString()} ₽</span>
                  )}
                </div>
              </div>

              {/* Form + Dosage selector */}
              <div className="p-3.5 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <FormDosageSelector
                  formTypes={productFormTypes as FormType[]}
                  selectedFormId={selectedFormId}
                  selectedDosageId={selectedDosageId}
                  color={color}
                  onFormChange={handleFormChange}
                  onDosageChange={handleDosageChange}
                />
              </div>

              {/* Quantity */}
              <div className="flex items-center gap-3">
                <div className="flex items-center rounded-xl overflow-hidden" style={{ background: '#0d0d1a', border: `1px solid ${color}28` }}>
                  <button onClick={() => setQuantity(q => Math.max(1, q - 1))} className="w-9 h-9 flex items-center justify-center cursor-pointer transition-colors text-white/50 hover:text-white text-lg"><i className="ri-subtract-line" /></button>
                  <div className="w-10 h-9 flex items-center justify-center font-orbitron font-bold text-base" style={{ color, borderLeft: `1px solid ${color}18`, borderRight: `1px solid ${color}18` }}>{quantity}</div>
                  <button onClick={() => setQuantity(q => Math.min(maxQty, q + 1))} disabled={!currentDosage.inStock} className="w-9 h-9 flex items-center justify-center cursor-pointer transition-colors text-white/50 hover:text-white text-lg disabled:opacity-30 disabled:cursor-not-allowed"><i className="ri-add-line" /></button>
                </div>
                <div className="flex-1">
                  <div className="font-mono text-xs text-white/30">Итого</div>
                  <div className="font-orbitron font-bold text-xl" style={{ color }}>{totalPrice.toLocaleString()} ₽</div>
                </div>
                {quantity >= 3 && (
                  <div className="font-mono text-[10px] px-2 py-1 rounded-full" style={{ background: 'rgba(57,255,20,0.08)', color: '#39ff14', border: '1px solid rgba(57,255,20,0.2)' }}>
                    Оптом
                  </div>
                )}
              </div>

              {/* Bonus */}
              <BonusBlock bonusPoints={product.bonusPoints} quantity={quantity} color={color} />

              {/* Price drop notify */}
              <PriceDropNotify color={color} productName={product.name} currentPrice={currentDosage.price} />

              {/* CTA Buttons */}
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => currentDosage.inStock && addItem({ id: product.id, name: product.name, price: currentDosage.price, oldPrice: currentDosage.oldPrice, image: product.images[0], color })}
                  disabled={!currentDosage.inStock}
                  className="whitespace-nowrap w-full py-3.5 rounded-xl font-orbitron font-bold text-sm tracking-wide cursor-pointer transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                  style={hasItem(product.id) ? { background: '#39ff14', color: '#050508', boxShadow: '0 0 22px #39ff1460' } : { background: color, color: '#050508', boxShadow: `0 0 22px ${color}60` }}
                >
                  <i className={hasItem(product.id) ? 'ri-check-double-line' : 'ri-shopping-bag-line'} />
                  {hasItem(product.id) ? 'Добавлено в корзину' : currentDosage.inStock ? 'Добавить в корзину' : 'Нет в наличии'}
                </button>

                <div className="flex gap-2">
                  <button
                    onClick={() => currentDosage.inStock && setShowOneClick(true)}
                    disabled={!currentDosage.inStock}
                    className="whitespace-nowrap flex-1 py-3 rounded-xl font-mono text-sm cursor-pointer transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ border: `2px solid ${color}`, color }}
                  >
                    <i className="ri-flashlight-line" />
                    Купить в 1 клик
                  </button>
                  <button
                    onClick={() => toggleFavorite({
                      id: product.id,
                      name: product.name,
                      price: currentDosage.price,
                      oldPrice: currentDosage.oldPrice,
                      image: product.images[0],
                      color,
                      category: 'Нейропротекторы',
                      rating: product.rating,
                      discount: product.discount,
                      inStock: product.inStock,
                    })}
                    className="w-[50px] h-[50px] rounded-xl cursor-pointer transition-all duration-300 flex items-center justify-center shrink-0"
                    style={{
                      border: isFavorite(product.id) ? '2px solid #ff2d78' : '2px solid rgba(255,255,255,0.09)',
                      background: isFavorite(product.id) ? 'rgba(255,45,120,0.12)' : 'transparent',
                      boxShadow: isFavorite(product.id) ? '0 0 12px rgba(255,45,120,0.3)' : 'none',
                    }}
                  >
                    <i
                      className={isFavorite(product.id) ? 'ri-heart-fill' : 'ri-heart-line'}
                      style={{ color: isFavorite(product.id) ? '#ff2d78' : 'rgba(255,255,255,0.35)', fontSize: '18px' }}
                    />
                  </button>
                </div>
              </div>

              {/* Infographic row */}
              <div className="flex gap-2 flex-wrap">
                {INFOGRAPHIC_ITEMS.map(item => (
                  <button key={item.id} onClick={() => setActiveInfographic(item)} className="flex items-center gap-1.5 px-2.5 py-2 rounded-xl cursor-pointer transition-all duration-200 hover:scale-105 relative" style={{ background: `${item.accentColor}07`, border: `1px solid ${item.accentColor}25`, flex: '1 0 auto', minWidth: '80px' }}>
                    {item.allowed !== null && (
                      <div className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 rounded-full flex items-center justify-center" style={{ background: item.allowed ? '#39ff14' : '#ff2d78' }}>
                        <i className={item.allowed ? 'ri-check-line' : 'ri-close-line'} style={{ color: '#050508', fontSize: '8px' }} />
                      </div>
                    )}
                    {item.allowed === null && (
                      <div className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 rounded-full flex items-center justify-center" style={{ background: item.accentColor }}>
                        <i className="ri-information-line" style={{ color: '#050508', fontSize: '8px' }} />
                      </div>
                    )}
                    <div className="w-4 h-4 flex items-center justify-center"><i className={item.icon} style={{ color: item.accentColor, fontSize: '15px' }} /></div>
                    <span className="font-mono text-[10px] text-white/45 whitespace-nowrap">{item.label}</span>
                  </button>
                ))}
              </div>

              {/* Delivery strips */}
              <div className="grid grid-cols-3 gap-2">
                {[
                  { icon: 'ri-truck-line', label: 'Доставка за 2 ч', sub: 'по Москве', color: '#00f5ff' },
                  { icon: 'ri-store-2-line', label: 'Самовывоз', sub: 'сегодня', color: '#39ff14' },
                  { icon: 'ri-shield-check-line', label: 'Гарантия', sub: 'оригинал', color: '#f0ff00' },
                ].map((item, i) => (
                  <div key={i} className="flex flex-col items-center gap-1 p-2.5 rounded-xl text-center" style={{ background: `${item.color}06`, border: `1px solid ${item.color}18` }}>
                    <div className="w-4 h-4 flex items-center justify-center"><i className={item.icon} style={{ color: item.color, fontSize: '15px' }} /></div>
                    <span className="font-mono text-[9px] text-white/50 leading-none">{item.label}</span>
                    <span className="font-mono text-[9px] text-white/28">{item.sub}</span>
                  </div>
                ))}
              </div>

              {/* Footer row */}
              <div className="flex items-center justify-between pt-0.5">
                <p className="font-mono text-[10px] text-white/20 leading-snug">
                  Информация носит справочный характер
                </p>
                <button onClick={() => setShowErrorReport(true)} className="whitespace-nowrap flex items-center gap-1 cursor-pointer transition-colors font-mono text-[10px] text-white/22 hover:text-orange-400 shrink-0 ml-3">
                  <i className="ri-error-warning-line" />
                  Нашли ошибку?
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ProductHero;
